/**
 * Strict browser-side contracts for the SharePoint library-selection surface.
 * Microsoft object identifiers stay opaque: the UI only compares and returns
 * them, while every displayed label and URL is validated before rendering.
 */

export interface SharePointSite {
  readonly id: string;
  readonly name: string;
  readonly displayName: string;
  readonly webUrl: string;
}

export interface SharePointDrive {
  readonly id: string;
  readonly siteId: string;
  readonly name: string;
  readonly webUrl: string;
  readonly driveType: string;
}

/**
 * One page of accessible sites. `truncated` is the service's own statement that
 * more sites exist than this page carries; the browser never derives a total
 * from it, so the UI can say "the first N are shown" without inventing a count.
 */
export interface SharePointSitePage {
  readonly sites: readonly SharePointSite[];
  readonly truncated: boolean;
}

export interface SharePointDriveSelection {
  readonly connectionId: string;
  readonly siteId: string;
  readonly siteName: string;
  readonly siteWebUrl: string;
  readonly driveId: string;
  readonly driveName: string;
  readonly driveWebUrl: string;
  readonly selectedAt: string;
  readonly updatedAt: string;
}

export class SharePointContractError extends Error {
  constructor() {
    super("The SharePoint library service returned an invalid response.");
    this.name = "SharePointContractError";
  }
}

function invalid(): never {
  throw new SharePointContractError();
}

function record(value: unknown): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return invalid();
  }
  return value as Record<string, unknown>;
}

/**
 * Exact keys, plus a named set that may be absent. Additive contract fields are
 * introduced this way so a browser can read both the version that sends them and
 * the version that does not; every other unknown key stays rejected.
 */
function boundedKeys(
  value: Record<string, unknown>,
  required: readonly string[],
  optional: readonly string[],
): void {
  const allowed = new Set([...required, ...optional]);
  if (Object.keys(value).some((key) => !allowed.has(key))) invalid();
  if (required.some((key) => !(key in value))) invalid();
}

function exactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): void {
  const actual = Object.keys(value).sort();
  const allowed = [...expected].sort();
  if (
    actual.length !== allowed.length ||
    actual.some((key, index) => key !== allowed[index])
  ) {
    invalid();
  }
}

function containsControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

function boundedString(value: unknown, maximum = 2_048): string {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > maximum ||
    containsControlCharacter(value)
  ) {
    return invalid();
  }
  return value;
}

function opaqueIdentifier(value: unknown): string {
  const identifier = boundedString(value);
  if (/[\\/?#]/u.test(identifier)) return invalid();
  return identifier;
}

function connectionIdentifier(value: unknown): string {
  const identifier = boundedString(value, 36).toLowerCase();
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(
      identifier,
    )
  ) {
    return invalid();
  }
  return identifier;
}

function webUrl(value: unknown): string {
  const raw = boundedString(value, 4_096);
  try {
    const parsed = new URL(raw);
    if (
      parsed.protocol !== "https:" ||
      parsed.username ||
      parsed.password ||
      parsed.hash
    ) {
      return invalid();
    }
    return parsed.toString();
  } catch {
    return invalid();
  }
}

function timestamp(value: unknown): string {
  const raw = boundedString(value, 64);
  if (!Number.isFinite(Date.parse(raw))) return invalid();
  return raw;
}

function valueArray(value: unknown): readonly unknown[] {
  const envelope = record(value);
  exactKeys(envelope, ["value"]);
  if (!Array.isArray(envelope.value)) return invalid();
  return envelope.value;
}

export function parseSharePointSite(value: unknown): SharePointSite {
  const site = record(value);
  exactKeys(site, ["id", "name", "displayName", "webUrl"]);
  return {
    id: opaqueIdentifier(site.id),
    name: boundedString(site.name, 320),
    displayName: boundedString(site.displayName, 320),
    webUrl: webUrl(site.webUrl),
  };
}

/**
 * The site page envelope. It keeps the `value` array every other SharePoint list
 * endpoint returns; `truncated` is additive and optional so this browser reads
 * both the service version that reports it and the one that does not, which is
 * what lets web and API ship independently. An absent field is not a claim that
 * the page is complete — it means no truncation was reported, and the dialog
 * then shows no truncation notice either way.
 */
export function parseSharePointSitePage(value: unknown): SharePointSitePage {
  const page = record(value);
  boundedKeys(page, ["value"], ["truncated"]);
  if (!Array.isArray(page.value)) return invalid();
  if (page.truncated !== undefined && typeof page.truncated !== "boolean") {
    return invalid();
  }
  return {
    sites: page.value.map(parseSharePointSite),
    truncated: page.truncated === true,
  };
}

export function parseSharePointDrive(value: unknown): SharePointDrive {
  const drive = record(value);
  exactKeys(drive, ["id", "siteId", "name", "webUrl", "driveType"]);
  return {
    id: opaqueIdentifier(drive.id),
    siteId: opaqueIdentifier(drive.siteId),
    name: boundedString(drive.name, 320),
    webUrl: webUrl(drive.webUrl),
    driveType: boundedString(drive.driveType, 128),
  };
}

export function parseSharePointDriveList(
  value: unknown,
): readonly SharePointDrive[] {
  return valueArray(value).map(parseSharePointDrive);
}

export function parseSharePointDriveSelection(
  value: unknown,
): SharePointDriveSelection {
  const selection = record(value);
  exactKeys(selection, [
    "connectionId",
    "siteId",
    "siteName",
    "siteWebUrl",
    "driveId",
    "driveName",
    "driveWebUrl",
    "selectedAt",
    "updatedAt",
  ]);
  return {
    connectionId: connectionIdentifier(selection.connectionId),
    siteId: opaqueIdentifier(selection.siteId),
    siteName: boundedString(selection.siteName, 320),
    siteWebUrl: webUrl(selection.siteWebUrl),
    driveId: opaqueIdentifier(selection.driveId),
    driveName: boundedString(selection.driveName, 320),
    driveWebUrl: webUrl(selection.driveWebUrl),
    selectedAt: timestamp(selection.selectedAt),
    updatedAt: timestamp(selection.updatedAt),
  };
}

export function parseSharePointDriveSelectionList(
  value: unknown,
): readonly SharePointDriveSelection[] {
  return valueArray(value).map(parseSharePointDriveSelection);
}
